import LegalLayout from "./legal/LegalLayout";
import {
  LEGAL_APP_ORIGIN,
  LEGAL_OPERATOR,
  LEGAL_SERVICE_NAME,
  LEGAL_SUPPORT_EMAIL,
} from "./legal/legal-meta";

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service & End User License Agreement"
      lede={`These terms are the agreement between you and ${LEGAL_OPERATOR} for using ${LEGAL_SERVICE_NAME}. They are written for a school setting and cover how you may use the Service, what you are responsible for, and when we may suspend or close an account.`}
      sibling={{ label: "Privacy Policy", to: "/privacy" }}
    >
      <section className="legal-section">
        <h2>1. Acceptance of these terms</h2>
        <p>
          By signing in to or using {LEGAL_SERVICE_NAME} (the "Service") at <code>{LEGAL_APP_ORIGIN}</code>,
          you agree to these Terms of Service and End User License Agreement (together, the "Terms"). If
          you do not agree, do not use the Service. These Terms incorporate our Privacy Policy by
          reference.
        </p>
      </section>

      <section className="legal-section">
        <h2>2. Who may use the Service</h2>
        <ul>
          <li>
            <strong>Teachers.</strong> Teacher accounts are for adults who are authorized by their school
            or district to use a classroom tool with students. If you create a teacher account on behalf
            of a school, you represent that you are allowed to accept these Terms for that use.
          </li>
          <li>
            <strong>Students.</strong> Student accounts are intended for use under the direction and
            supervision of a teacher and school. Students use the Service as part of their schoolwork.
          </li>
          <li>
            <strong>Children.</strong> The Service is designed for school use, including with students
            under 13, on the basis that the school or teacher provides consent for that educational use
            as described in the Privacy Policy.
          </li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>3. License to use the Service</h2>
        <p>
          Subject to these Terms, {LEGAL_OPERATOR} grants you a limited, non-exclusive, non-transferable,
          revocable license to access and use the Service for legitimate educational purposes. This
          license does not transfer any ownership of the Service to you, and all rights not expressly
          granted are reserved by {LEGAL_OPERATOR}.
        </p>
      </section>

      <section className="legal-section">
        <h2>4. Your account</h2>
        <ul>
          <li>Sign in with your own school Google account and keep your access secure.</li>
          <li>Do not share your account, and do not use someone else's account.</li>
          <li>Provide accurate information and choose the correct role (teacher or student).</li>
          <li>Tell us promptly if you believe your account has been accessed without permission.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>5. Acceptable use</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>Break the law or encourage others to break the law.</li>
          <li>
            Harass, bully, threaten, demean, or harm anyone, or post content that is hateful, sexually
            explicit, or otherwise inappropriate for a school.
          </li>
          <li>
            Attempt to bypass, disable, or abuse security controls, the code sandbox, usage limits, or
            access restrictions.
          </li>
          <li>
            Use the code editor, automated checks, or AI features to attack, overload, scan, or gain
            unauthorized access to any system or network.
          </li>
          <li>Upload or run malware, or attempt to mine cryptocurrency or otherwise misuse compute.</li>
          <li>
            Reverse engineer, scrape, or copy the Service except as allowed by law, or resell or
            sublicense access.
          </li>
          <li>Infringe anyone's intellectual property or privacy, or impersonate others.</li>
          <li>Interfere with other users' ability to use the Service.</li>
        </ul>
        <p>
          Teachers are responsible for supervising their students' use and for the lessons and content
          they create and assign.
        </p>
      </section>

      <section className="legal-section">
        <h2>6. Your content</h2>
        <p>
          You keep ownership of the content you create, including lessons, prompts, code, and files
          ("Your Content"). You grant {LEGAL_OPERATOR} a non-exclusive, worldwide license to host, store,
          copy, display, and process Your Content solely to operate and provide the Service to you and
          your classroom. You are responsible for having the rights to the content you upload, and for
          making sure it is appropriate for a school setting.
        </p>
      </section>

      <section className="legal-section">
        <h2>7. AI lesson-authoring features</h2>
        <p>
          Teacher AI tools generate draft lessons and edits using a third-party model. Output may be
          inaccurate, incomplete, or unsuitable, and is not a substitute for a teacher's professional
          judgment. Teachers must review and edit AI drafts before using them with students. We do not
          guarantee that AI output is correct, original, or fit for any particular purpose.
        </p>
      </section>

      <section className="legal-section">
        <h2>8. Subscriptions, trials, and fees</h2>
        <p>
          Some teacher features require a paid subscription. New teachers get a 15-day app-managed free
          trial (no card required) with limited cohorts (2 active, up to 15 students each, 30 total),
          lessons, and AI usage as shown in the app. Paid plans include up to 6 active cohorts (unlimited
          concluded cohorts), up to 40 students per cohort, and 240 students total, plus higher AI
          limits. When you subscribe, checkout is processed by Stripe and your paid plan starts
          immediately. Pricing, billing periods, and limits are shown in the app before you subscribe.
          Unless stated otherwise, subscriptions renew automatically until cancelled, and you can cancel
          from the billing area to stop future renewals. Fees already paid are non-refundable except where
          required by law. Students are never charged.
        </p>
      </section>

      <section className="legal-section">
        <h2>9. Suspension and termination</h2>
        <p>
          We want the Service to be safe for classrooms. We may suspend or terminate access to an
          account, or remove content, if we reasonably believe that:
        </p>
        <ul>
          <li>these Terms or the acceptable-use rules have been violated;</li>
          <li>
            an account is being used in a way that harms or threatens students, other users, the Service,
            or others, or that creates legal or security risk;
          </li>
          <li>we are required to do so by law or a valid legal request; or</li>
          <li>a teacher's subscription has lapsed (for paid features) or the account is inactive.</li>
        </ul>
        <p>
          Where it is reasonable and lawful to do so, we will give notice and, for less serious issues, a
          chance to fix the problem before we suspend or terminate. When there is a risk to safety,
          security, or the integrity of the Service, we may suspend access immediately and review
          afterward. We will apply these measures reasonably and proportionately to the issue.
        </p>
        <p>
          A school or teacher may also remove a student's access, and you may stop using the Service and
          ask us to close your account at any time. After termination, your license to use the Service
          ends; we handle any remaining data as described in the Privacy Policy.
        </p>
      </section>

      <section className="legal-section">
        <h2>10. Service availability and changes</h2>
        <p>
          We work to keep the Service available, but we may modify, suspend, or discontinue features, and
          the Service may occasionally be unavailable for maintenance or for reasons beyond our control.
          We are not liable for any unavailability or for changes to features.
        </p>
      </section>

      <section className="legal-section">
        <h2>11. Disclaimers</h2>
        <p>
          The Service is provided "as is" and "as available" without warranties of any kind, whether
          express or implied, including any implied warranties of merchantability, fitness for a
          particular purpose, and non-infringement. We do not warrant that the Service will be
          uninterrupted, error-free, or secure, or that any content or AI output is accurate.
        </p>
      </section>

      <section className="legal-section">
        <h2>12. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, {LEGAL_OPERATOR} will not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or for any loss of data, goodwill, or
          profits, arising from your use of the Service. To the maximum extent permitted by law, our
          total liability for any claim relating to the Service will not exceed the greater of the amount
          you paid us for the Service in the twelve months before the claim, or USD $100.
        </p>
      </section>

      <section className="legal-section">
        <h2>13. Indemnification</h2>
        <p>
          To the extent permitted by law, you agree to defend and indemnify {LEGAL_OPERATOR} against
          third-party claims and costs arising from your misuse of the Service or your violation of these
          Terms or of someone else's rights. This does not apply where prohibited by law, including for
          public schools or government entities that cannot agree to indemnification.
        </p>
      </section>

      <section className="legal-section">
        <h2>14. Governing law</h2>
        <p>
          These Terms are governed by the laws of the State of Washington, United States, without regard
          to its conflict-of-laws rules, except where mandatory local law (including the law that applies
          to a public school) requires otherwise. The state and federal courts located in Washington will
          have jurisdiction over disputes that are not otherwise resolved.
        </p>
      </section>

      <section className="legal-section">
        <h2>15. Changes to these Terms</h2>
        <p>
          We may update these Terms as the Service evolves or the law changes. We will update the "Last
          updated" date above and, for material changes, make reasonable efforts to notify teachers.
          Continuing to use the Service after changes take effect means you accept the updated Terms.
        </p>
      </section>

      <section className="legal-section">
        <h2>16. Contact</h2>
        <p>
          Questions about these Terms? Email{" "}
          <a href={`mailto:${LEGAL_SUPPORT_EMAIL}`}>{LEGAL_SUPPORT_EMAIL}</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
